import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import {
  DEFAULT_PIPELINE,
  INACTIVE_STAGE_COLOR,
  getPipelineStages,
  stageColor,
  type PipelineStage,
} from '../lib/programPipeline';

/**
 * Master pipeline chevron bar.
 *
 * Now renders the stages of the *selected program* rather than one hardcoded
 * list shared by every program. Pass either:
 *   • `program` — any object with a `pipeline` array (a ProgramConfig), or
 *   • `stages`  — an explicit stage list.
 *
 * With neither, it falls back to the platform default, so existing call sites
 * behave exactly as before.
 *
 * Two latent bugs are fixed along the way:
 *   • the colour ramp was a fixed array of 10 hex values, so an 11th stage
 *     rendered with `undefined` fill (transparent). Colours are interpolated
 *     for any stage count now.
 *   • chevron width was fixed at 148px regardless of label length.
 */

/**
 * @deprecated Import `DEFAULT_PIPELINE` from `src/lib/programPipeline` and
 * resolve the program's own stages with `getPipelineStages(program)`.
 * Re-exported so the 17 existing call sites keep compiling during migration.
 */
export const MASTER_STAGES = DEFAULT_PIPELINE;

interface PipelineOwner {
  pipeline?: PipelineStage[];
  defaultStageId?: string;
}

interface MasterPipelineProps {
  currentStage: number;
  onStageChange?: (stage: number) => void;
  readOnly?: boolean;
  /** Program whose pipeline should be rendered. */
  program?: PipelineOwner | null;
  /** Explicit stages — takes precedence over `program`. */
  stages?: PipelineStage[];
}

export default function MasterPipeline({
  currentStage,
  onStageChange,
  readOnly = false,
  program,
  stages: explicitStages,
}: MasterPipelineProps) {
  const stages = explicitStages?.length ? explicitStages : getPipelineStages(program);

  const CHEVRON_W = 148;
  const CHEVRON_H = 64;
  const NOTCH = 20;

  if (stages.length === 0) return null;

  return (
    <div
      className="w-full overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50/80 p-2"
      role="navigation"
      aria-label="Application pipeline stages"
    >
      <div className="flex items-stretch min-w-max">
        {stages.map((stage, idx) => {
          const isCompleted = idx < currentStage;
          const isCurrent = idx === currentStage;
          const isFuture = idx > currentStage;
          const isFirst = idx === 0;
          const isLast = idx === stages.length - 1;

          const bgColor = isFuture
            ? INACTIVE_STAGE_COLOR
            : stage.color || stageColor(idx, stages.length);

          let points: string;
          const w = CHEVRON_W;
          const h = CHEVRON_H;
          const n = NOTCH;

          if (isFirst) {
            points = `0,0 ${w - n},0 ${w},${h / 2} ${w - n},${h} 0,${h}`;
          } else if (isLast) {
            points = `0,0 ${w},0 ${w},${h} 0,${h} ${n},${h / 2}`;
          } else {
            points = `${n},0 ${w - n},0 ${w},${h / 2} ${w - n},${h} 0,${h} ${n},${h / 2}`;
          }

          const labelStyle: React.CSSProperties = {
            maxWidth: isFirst ? w - n - 12 : w - 2 * n - 8,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          };

          return (
            <div
              key={stage.id}
              style={{
                width: CHEVRON_W,
                height: CHEVRON_H,
                flexShrink: 0,
                marginLeft: isFirst ? 0 : -(n - 1),
                position: 'relative',
                cursor: readOnly ? 'default' : 'pointer',
                zIndex: isCurrent ? 10 : stages.length - idx,
                transition: 'filter 0.2s ease, transform 0.2s ease',
              }}
              className={
                !readOnly
                  ? 'hover:brightness-95 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 rounded-xl'
                  : ''
              }
              onClick={() => {
                if (!readOnly && onStageChange) onStageChange(idx);
              }}
              role={readOnly ? undefined : 'button'}
              tabIndex={readOnly ? undefined : 0}
              onKeyDown={(event) => {
                if (!readOnly && onStageChange && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onStageChange(idx);
                }
              }}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={`${stage.label}: ${
                isCurrent ? 'Current stage' : isCompleted ? 'Completed' : 'Upcoming'
              }`}
              title={
                stage.description || (readOnly ? stage.label : `Jump to phase: ${stage.label}`)
              }
            >
              <svg
                width={CHEVRON_W}
                height={CHEVRON_H}
                viewBox={`0 0 ${CHEVRON_W} ${CHEVRON_H}`}
                style={{ display: 'block', position: 'absolute', inset: 0 }}
                aria-hidden="true"
              >
                <polygon
                  points={points}
                  fill={bgColor}
                  stroke={isCurrent ? 'rgba(0,0,0,0.12)' : 'none'}
                  strokeWidth={isCurrent ? 1 : 0}
                />
              </svg>

              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingLeft: isFirst ? 10 : n + 4,
                  paddingRight: isLast ? 10 : n + 4,
                  pointerEvents: 'none',
                }}
              >
                <span
                  style={{
                    ...labelStyle,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                    color: isFuture ? '#8C9BB5' : '#fff',
                    lineHeight: 1.2,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  {stage.label}
                </span>
                <span
                  style={{
                    fontSize: 8,
                    fontWeight: 700,
                    color: isFuture ? '#8C9BB5' : 'rgba(255,255,255,0.88)',
                    marginTop: 4,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  {isCompleted && <CheckCircle2 size={10} aria-hidden="true" />}
                  {isCurrent ? 'Current' : isCompleted ? 'Done' : 'Jump'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
